import {useAppSelector} from '../../store/store';
import {Status} from '../../enums/status';
import styles from './dashboard.module.scss';
import {CenteredSpinner} from '../../components/spinner/centered_spinner';
import {Hero} from '../../components/hero/hero.tsx';
import {Page} from '../../components/page/page.tsx';
import {PageTitle} from '../../components/page/page_title.tsx';
import {__} from '../../support/lang/translations.ts';
import {CardGrid} from '../../components/card/card_grid.tsx';
import {AltButton} from '../../components/button/alt_button.tsx';
import {Main} from '../../components/main/main.tsx';
import {PageContent} from '../../components/page/page_content.tsx';
import {WidgetWrapper} from '../../components/widget/widget_wrapper.tsx';
import {TemperatureWidget} from './widgets/temperature_widget.tsx';
import {UnreadMessagesWidget} from './widgets/unread_messages_widget.tsx';
import {NotificationsDisabledWidget} from './widgets/notifications_disabled_widget.tsx';
import {VehicleCard} from '../../components/vehicle/vehicle_card.tsx';
import {HideElement} from '../../components/utility/hide_element_wrapper.tsx';
import {ShowElement} from '../../components/utility/show_element_wrapper.tsx';
import {UserHero} from './elements/user_hero.tsx';
import {VehiclePlaceholder} from '../../components/vehicle/vehicle_placeholder.tsx';
import {VehiclesSlider} from './elements/vehicles_slider.tsx';
import {FeaturesSlider} from './elements/features_slider.tsx';
import {Support} from './elements/support.tsx';
import {UserIntro} from './elements/user_intro.tsx';
import {unreadNotificationsCountSelector} from '../../store/slices/notifications_slice.ts';
import {UnreadNotificationsWidget} from './widgets/unread_notifications_widget.tsx';
import {unreadMessagesCountSelector} from '../../store/slices/chat_slice.ts';
import {NotificationPermissionsDisabledWidget} from './widgets/notification_permission_disabled_widget.tsx';

export function Dashboard() {
    const customerInfo = useAppSelector((state) => state.customerInfo);
    const appConfig = useAppSelector((state) => state.appConfig.appconfig);

    const unreadNotificationsCount = useAppSelector(unreadNotificationsCountSelector);
    const unreadMessagessCount = useAppSelector(unreadMessagesCountSelector);

    const organisation = useAppSelector((state) => state.appConfig.organisation);
    const meData = useAppSelector((state) => state.customerInfo.me?.customer);
    const locations = useAppSelector((state) => state.appConfig.locations);

    const defaultLocationId = meData?.default_location_id;
    const defaultLocation = locations
        ? defaultLocationId
            ? locations?.filter((l) => l?.id === defaultLocationId)[0]
            : locations.length > 0
              ? locations[0]
              : null
        : null;

    let whatsAppNumber = organisation?.whatsapp_number;
    if (defaultLocation != null && defaultLocation.whatsapp_number != null) {
        whatsAppNumber = defaultLocation.whatsapp_number;
    }

    if (customerInfo.status !== Status.FULFILLED) {
        return <CenteredSpinner />;
    }

    const vehicles = customerInfo.me?.customer?.vehicles;

    return (
        <Main>
            <div className={styles.dashboard}>
                <HideElement size={'md'}>
                    <Hero
                        wide={true}
                        url={appConfig?.loginscreen}
                        urls={[appConfig?.sliderphoto1, appConfig?.sliderphoto2, appConfig?.sliderphoto3]}
                    >
                        <UserIntro customerAccount={customerInfo.me} companyName={appConfig?.appname} />
                    </Hero>
                    <Page style={{paddingTop: 0, paddingBottom: 0}}>
                        {/*WIDGETS Desktop*/}
                        <PageContent wide={true} style={{paddingBottom: 0}}>
                            <WidgetWrapper>
                                {/*TODO: Make functional*/}
                                <TemperatureWidget />
                                <UnreadMessagesWidget unreadCount={unreadMessagessCount} />
                                <UnreadNotificationsWidget unreadCount={unreadNotificationsCount} />
                                <NotificationsDisabledWidget />
                                <NotificationPermissionsDisabledWidget />
                            </WidgetWrapper>
                        </PageContent>
                    </Page>
                </HideElement>

                <ShowElement size={'md'}>
                    <Page style={{paddingTop: 0}}>
                        {/*WIDGETS mobile*/}
                        <PageContent wide={true}>
                            <UserHero customerAccount={customerInfo.me}></UserHero>
                            <WidgetWrapper>
                                <TemperatureWidget />
                                <UnreadMessagesWidget />
                                <NotificationsDisabledWidget />
                                <NotificationPermissionsDisabledWidget />
                            </WidgetWrapper>
                        </PageContent>
                    </Page>
                    <Hero
                        url={appConfig?.loginscreen}
                        urls={[appConfig?.sliderphoto1, appConfig?.sliderphoto2, appConfig?.sliderphoto3]}
                    >
                        <UserIntro customerAccount={customerInfo.me} companyName={appConfig?.appname} />
                        <ShowElement size={'sm'}>
                            <Support
                                whatsAppNumber={whatsAppNumber}
                                to={'/chat'}
                                name={appConfig?.appname ?? 'Medewerker'}
                                src={appConfig?.appicon ?? undefined}
                            />
                        </ShowElement>
                    </Hero>
                </ShowElement>

                <HideElement size={'sm'}>
                    <Page>
                        <PageContent wide={true}>
                            <PageTitle>
                                <h2 className="mr-auto">{__('dashboard.titles.vehicles')}</h2>
                                <AltButton to="vehicles">
                                    <i className="fa-light fa-chevron-right" />
                                    {__('dashboard.buttons.vehicles')}
                                </AltButton>
                            </PageTitle>

                            <CardGrid>
                                {vehicles && vehicles?.length > 0 ? (
                                    vehicles.map((vehicle, index) => {
                                        if (!vehicle) {
                                            return null;
                                        }
                                        return <VehicleCard vehicle={vehicle} key={`${vehicle.id}-${index}`} />;
                                    })
                                ) : (
                                    <>
                                        <VehiclePlaceholder style={{opacity: 0.7}} />
                                        <VehiclePlaceholder style={{opacity: 0.5}} />
                                        <VehiclePlaceholder style={{opacity: 0.25}} />
                                    </>
                                )}
                            </CardGrid>
                        </PageContent>
                    </Page>
                </HideElement>

                <ShowElement size={'sm'}>
                    <VehiclesSlider vehicles={vehicles} />
                </ShowElement>

                <ShowElement size={'sm'}>
                    <FeaturesSlider />
                </ShowElement>
            </div>

            <HideElement size={'sm'}>
                <Support
                    whatsAppNumber={whatsAppNumber}
                    to={'/chat'}
                    name={appConfig?.appname ?? 'Medewerker'}
                    src={appConfig?.appicon ?? undefined}
                />
            </HideElement>
        </Main>
    );
}
